import appconfig from "../../../appconfig";
import ProductSelection from "../../../domains2/orders/ProductSelection";
import { convertProductSelectionsToMap } from "../../../domains2/orders/OrderLineMappingHelper";
import {evaluateCustomerType, getExceededPriceRules} from "../../../libraries/utils";

export const remapGTMReceiptLines = function (receiptLines, orderLines) {
	// we use these remapped items in different states for cart and orders
	// since they hold the most necessary data for GTM
	return JSON.stringify(receiptLines.map((rl) => {
		const ol = orderLines.find((ol) => {
			return rl.productId === ol.productId;
		});

		if (!ol) {
			return undefined;
		}

		let discount;
		const voucherDiscount = ol.subLines.find(sl => sl.type === "Voucher");
		const appliedVoucherId = voucherDiscount && voucherDiscount.values[0].voucherId;

		if (appliedVoucherId) {
			discount = ol.applicableDiscounts.find(ad => ad.voucherPattern.includes(appliedVoucherId));
		} else {
			discount = rl.priceBefore > rl.price ? ol.discount : null;
		}

		return {
			...rl,
			// in most cases we should use name from orderLine, receiptLine adds quantity and should be used only if ol
			// doesn't exist
			name: ol && ol.name ? ol.name : rl.name,
			category: ol && ol.category
				? ol.category.name
				: ol.options && ol.options.appliedPriceRule && ol.options.appliedPriceRule.options && ol.options.appliedPriceRule.options.label,
			discount: discount
		};
	}).filter(Boolean));
};
export const prepareGTMeCommerceData = function (rl = null) {
	const receiptLines = rl || JSON.parse(localStorage.getItem("gtmReceiptLines"));
	const productArr = [];
	let cartTotal = 0;
	try {
		receiptLines.forEach((receiptLine) => {
			const quantity = receiptLine.quantity ? receiptLine.quantity : 1;
			const prodUnitPrice = parseFloat(receiptLine.totalPriceAccum) ? parseFloat(receiptLine.totalPriceAccum) : parseFloat(receiptLine.totalPrice);
			const pantValue = getPantValue(receiptLine, quantity);
			const discount = receiptLine.discount;
			productArr.push({
				product: {
					id: receiptLine.productId,
					name: receiptLine.name ? receiptLine.name : receiptLine.text,
					quantity: quantity,
					discount: discount,
					price: receiptLine.price
				},
				productPrice: {
					basePrice: (prodUnitPrice - pantValue) / quantity
				},
				category: receiptLine.category ? receiptLine.category : ""
			});
			cartTotal += parseFloat(prodUnitPrice - pantValue);
		});
		return {items: productArr, value: cartTotal};
	} catch (e) {
		// even if pushing to dataLayer fails, we want to continue execution
		return {items: [], value: 0};
	}
};

const copyObject = function(source) {
	return JSON.parse(JSON.stringify(source));
};
export const getGTMTotalPrice = (products) => {
	let total = 0;
	for(let i = 0; i < products.length; i++) {
		total += products[i].product.price * (products[i].product.quantity || 1);
	}
	return total;
};

export const getGTMFlattenMultiQuantityProductSelection = (mainProducts, productSelections = []) => {
	const products = [];
	// make a copy of product selections, it will be mutated in the later stage
	const productSelectionsCopy = copyObject(productSelections);
	try {
		mainProducts.forEach((mainProduct, index) => {
			products.push({
				product: {
					id: mainProduct.productId,
					price: mainProduct.price,
					name: mainProduct.name.replace(/\d+\sx\s/,""),
					quantity: mainProduct.quantity || 1
				}
			});
			const productGroups = productSelectionsCopy[index] && productSelectionsCopy[index].groups;
			mainProduct.description.forEach((item) => {
				if(item.text && item.text.includes("(+")) {
					const match = item.text.match(/\(([\d.,+-]+)\)/);
					// check in which group from product selection current product exists
					// when found, we will use product group type as item_category2
					const group = findGroupContainingProduct(productGroups, item.productId);
					if(group) {
						// filter out product when found in a group from selected products, so that next iteration doesn't
						// pick up the wrong one (if the same product is contained in multiple product groups)
						group.selectedProducts = filterSelectedProductsFromGroup(group, item.productId);
					}
					products.push({
						product: {
							id: item.productId,
							price: (match ? parseFloat(match[1].replace(",", ".")) : item.price) / (item.quantity || 1),
							name: item.textRaw,
							quantity: item.quantity,
							item_category2: group && group.options.groupType
						}
					});
				}
			});
		});
	} catch (e) {
		//
	}
	return products;
};
export const getGTMFlattenSingleProductSelection = (mainProduct, quantity = 1, productGroups = []) => {
	const products = [];
	const productGroupsCopy = copyObject(productGroups);
	try {
		products.push({
			product: {
				id: mainProduct.productId,
				price: mainProduct.price,
				name: mainProduct.name.replace(/\d+\sx\s/,""),
				quantity: quantity
			}
		});
		mainProduct.description.forEach((item) => {
			if(item.text && item.text.includes("(+")) {
				const match = item.text.match(/\(([\d.,+-]+)\)/);
				const group = findGroupContainingProduct(productGroupsCopy, item.productId);
				if(group) {
					group.selectedProducts = filterSelectedProductsFromGroup(group, item.productId);
				}
				products.push({
					product: {
						id: item.productId,
						price: (match ? parseFloat(match[1].replace(",", ".")) : item.price) / (item.quantity || 1),
						name: item.textRaw,
						quantity: item.textRaw === "Pant" ? item.quantity * quantity : item.quantity,
						item_category2: group && group.options.description
					}
				});
			}
		});
	} catch (e) {
		//
	}
	return products;
};

const findGroupContainingProduct = (groups, productId) => {
	return (groups || []).find(group => {
		return (group.selectedProducts || []).find(p => {
			if(typeof p === "string") {
				return p === productId;
			}
			return p.productId === productId;
		});
	});
};
const filterSelectedProductsFromGroup = (group, productId) => {
	return group.selectedProducts.filter(p => {
		if(typeof p === "string") {
			return p !== productId;
		}
		return p.productId !== productId;
	});
};
const getPantValue = function (receiptLine, quantity) {
	const pant = receiptLine.description.find(item => item.textRaw === "Pant");
	if(pant) {
		return pant.price * quantity;
	}
	return 0;
};

export const generateOrderLines = (self, {productCatalog, productSelections, isRootProductMenu = undefined}) => {
	if(!productSelections || !Array.isArray(productSelections) || productSelections.length === 0) {
		return [];
	}
	const getters = self.$store.getters;
	return ProductSelection.mapToOrderLinesGx(appconfig.getBusinessModelId())(
		productCatalog,
		productSelections,
		false,
		{
			vouchers: getters.vouchersRedeemed,
			employeeDiscountToken: getters.employeeDiscountToken,
			sunsetPoints: getters.sunsetPoints,
			dailySpending: getters.featureToggles.discountDailyLimit
				? getters.applicationUserData && getters.applicationUserData.meta.dailySpending
				: {},
			channel: appconfig.channelAlias(),
			deliveryMethod: getters.tenantSpecificData.deliveryChoice,
			customerType: evaluateCustomerType(),
			cartProductsMap: convertProductSelectionsToMap(getters.cartProducts),
			exceededPriceRules: getExceededPriceRules(),
			isRootProductMenu
		},
		getters.tenantSpecificData.cartOrderTags
	)
		.filter(ol => ol.type !== "Meta");
};
